import { drawPiece } from './pieceRenderer';

/**
 * Отрисовывает доску пазла на *канвасе доски*, используя offscreen canvas для кеширования фона и размещенных кусков.
 * Канвас доски теперь имеет размер самой доски (boardRect).
 * @param {CanvasRenderingContext2D} ctx - Контекст *канваса доски*.
 * @param {object | null} boardArea - Не используется. Передается null.
 * @param {object} store - Zustand store.
 * @param {object} [options={}] - Опции отрисовки.
 * @param {boolean} [options.lite=false] - Если true, использовать кешированный канвас без перерисовки неразмещенных кусков.
 */
export function drawBoard(ctx, boardArea, store, options = {}) {
  const { ref } = store;
  const { puzzle } = ref;
  const { state } = puzzle;
  const { pieces, imageDimensions } = state;

  // Получаем размеры канваса доски (теперь они должны совпадать с boardDimensions)
  const boardCanvasDimensions = ref.puzzle.boardCanvas?.dimensions;
  if (!boardCanvasDimensions || boardCanvasDimensions.width <= 0 || boardCanvasDimensions.height <= 0) {
    return; // Ничего не рисуем, если канвас не готов
  }

  // Размеры логической доски (для консистентности, должны совпадать с размерами канваса)
  const boardDims = state.boardDimensions;

  ctx.clearRect(0, 0, boardCanvasDimensions.width, boardCanvasDimensions.height);

  // Проверяем наличие размеров логической доски и изображения
  if (!boardDims || boardDims.width <= 0 || boardDims.height <= 0 || !imageDimensions?.width) {
    // Рисуем плейсхолдер на ВЕСЬ канвас доски
    drawPlaceholder(ctx, { x: 0, y: 0, width: boardCanvasDimensions.width, height: boardCanvasDimensions.height });
    console.warn('drawBoard: boardDimensions or image dimensions not available. Drawing placeholder.');
    return;
  }

  // Инициализируем кеш доски
  puzzle.board = puzzle.board || {};
  const boardCache = puzzle.board;
  const dpr = window.devicePixelRatio || 1;
  // --- Размеры кеша = размеры логической доски (канваса доски) ---
  const cacheWidth = boardDims.width;
  const cacheHeight = boardDims.height;

  // --- Проверка валидности кеша ---
  const isCacheValid =
    boardCache.canvas &&
    boardCache.renderedWidth === cacheWidth &&
    boardCache.renderedHeight === cacheHeight &&
    boardCache.renderedDpr === dpr;

  // --- Lite Режим (Использование Валидного Кеша) ---
  if (options.lite && isCacheValid) {
    // <<< Рисуем кеш в начало канваса доски (0,0) >>>
    ctx.drawImage(
      boardCache.canvas,
      0, // dx = 0
      0, // dy = 0
      cacheWidth,
      cacheHeight
    );
    // Рисуем НЕРАЗМЕЩЕННЫЕ куски ПОВЕРХ кеша
    drawBoardPieces(ctx, store); // <<< Убираем boardRect, т.к. рисуем от (0,0)
    return; // Выходим
  }

  // --- Полная Перерисовка или Обновление Кеша ---
  const boardOffscreenCtx = prepareOffscreenCanvas(boardCache, cacheWidth, cacheHeight, dpr);

  if (!boardOffscreenCtx) {
      console.warn("drawBoard could not prepare offscreen canvas.");
      // Рисуем плейсхолдер на весь канвас доски
      drawPlaceholder(ctx, { x: 0, y: 0, width: boardCanvasDimensions.width, height: boardCanvasDimensions.height });
      return;
  }

  // --- Рисование на Offscreen Canvas (Кеше) ---
  boardOffscreenCtx.clearRect(0, 0, cacheWidth, cacheHeight); // Очищаем кеш
  boardOffscreenCtx.save();

  // 1. Рисуем фон-подсказку на кеше
  drawBoardBackgroundHint(boardOffscreenCtx, puzzle, cacheWidth, cacheHeight, dpr);

  // 2. Рисуем РАЗМЕЩЕННЫЕ куски на кеше
  drawPlacedBoardPieces(boardOffscreenCtx, store);

  boardOffscreenCtx.restore(); // Восстанавливаем состояние кеш-контекста

  // --- Копирование Кеша на Канвас Доски ---
  // <<< Рисуем кеш в начало канваса доски (0,0) >>>
  ctx.drawImage(
      boardCache.canvas,
      0, // dx = 0
      0, // dy = 0
      cacheWidth,
      cacheHeight
  );

  // --- Рисуем НЕРАЗМЕЩЕННЫЕ куски ПОВЕРХ кеша на канвасе доски ---
  drawBoardPieces(ctx, store); // <<< Убираем boardRect, т.к. рисуем от (0,0)
}

// --- Вспомогательные функции ---

/**
 * Рисует неразмещенные пазлы, находящиеся НА ДОСКЕ (onBoard=true, placed=false).
 * Рисует на основном контексте канваса доски. Координаты (0,0) контекста = верхний левый угол доски.
 * @param {CanvasRenderingContext2D} ctx - Контекст канваса доски.
 * @param {object} store - Zustand store.
 */
// <<< Убираем boardRect из параметров >>>
function drawBoardPieces(ctx, store) {
    const { state } = store.ref.puzzle;
    const { pieces, draggingPiece } = state;

    // Фильтруем куски: на доске, не размещены, не перетаскиваются сейчас
    const pieceIdsToDraw = Object.keys(pieces || {}).filter(pieceId => {
        const piece = pieces[pieceId];
        return piece?.onBoard && !piece.placed && draggingPiece?.pieceId !== pieceId;
    }).sort((idA, idB) => (pieces[idA].zIndex || 40) - (pieces[idB].zIndex || 40));

    // Сохраняем состояние контекста канваса доски
    ctx.save();
    // <<< Убираем ctx.translate(boardRect.x, boardRect.y) >>>
    // Теперь (0,0) контекста - это уже начало доски

    // Рисуем каждый неразмещенный кусок
    for (const pieceId of pieceIdsToDraw) {
        // Контекст 'board', координаты piece.currentPos уже относительно (0,0) доски
        drawPiece(ctx, pieceId, store, { context: 'board' });
    }

    // Восстанавливаем систему координат
    ctx.restore();
}

/**
 * Рисует ТОЛЬКО размещенные (placed) пазлы на заданном контексте (обычно offscreen кеш).
 * Координаты (0,0) этого контекста соответствуют левому верхнему углу boardRect.
 * @param {CanvasRenderingContext2D} targetCtx - Контекст для рисования (offscreen).
 * @param {object} store - Zustand store.
 */
function drawPlacedBoardPieces(targetCtx, store) {
    // Логика остается прежней, т.к. она рисует на кеше от (0,0)
    const { state } = store.ref.puzzle;
    const { pieces } = state;

    const pieceIdsToDraw = Object.keys(pieces || {}).filter(pieceId => {
        return pieces[pieceId]?.placed;
    }).sort((idA, idB) => (pieces[idA].zIndex || 30) - (pieces[idB].zIndex || 30));

    for (const pieceId of pieceIdsToDraw) {
        drawPiece(targetCtx, pieceId, store, { context: 'board' });
    }
}


/**
 * Рисует простой плейсхолдер для области.
 * @param {CanvasRenderingContext2D} ctx - Контекст для рисования.
 * @param {object} area - Область { x, y, width, height }.
 */
// Функция без изменений
function drawPlaceholder(ctx, area) {
    if (!area || area.width <= 0 || area.height <= 0) return;
    ctx.save();
    ctx.fillStyle = 'rgba(200, 200, 220, 0.3)';
    ctx.fillRect(area.x, area.y, area.width, area.height);
    ctx.strokeStyle = 'grey';
    ctx.lineWidth = 1;
    ctx.strokeRect(area.x + 0.5, area.y + 0.5, area.width - 1, area.height - 1); // Смещаем на полпикселя для четкости
    ctx.fillStyle = 'grey';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Board Area', area.x + area.width / 2, area.y + area.height / 2);
    ctx.restore();
}

/**
 * Отрисовывает фон-подсказку (изображение и рамку) на заданном контексте (offscreen кеш).
 * @param {CanvasRenderingContext2D} targetCtx - Контекст для рисования (offscreen).
 * @param {object} puzzle - ref.puzzle из store.
 * @param {number} width - Ширина области фона (ширина boardRect).
 * @param {number} height - Высота области фона (высота boardRect).
 * @param {number} dpr - Device Pixel Ratio.
 */
// Функция без изменений
function drawBoardBackgroundHint(targetCtx, puzzle, width, height, dpr) {
    const { state, config } = puzzle;
    const puzzlePreviewOpacity = config.game?.puzzlePreviewOpacity ?? 0.25;
    const { puzzleImageElement } = state;

    targetCtx.save();
    if (puzzleImageElement && puzzleImageElement.complete && puzzleImageElement.naturalWidth > 0) {
        targetCtx.globalAlpha = puzzlePreviewOpacity;
        targetCtx.drawImage(puzzleImageElement, 0, 0, width, height);
        targetCtx.globalAlpha = 1.0;

        targetCtx.strokeStyle = 'rgba(128, 128, 128, 0.6)';
        targetCtx.lineWidth = 1;
        targetCtx.setLineDash([4, 2]);
        targetCtx.strokeRect(0.5, 0.5, width - 1, height - 1);
        targetCtx.setLineDash([]);
    } else {
        targetCtx.fillStyle = 'grey';
        targetCtx.font = '12px sans-serif';
        targetCtx.textAlign = 'center';
        targetCtx.textBaseline = 'middle';
        targetCtx.fillText('⏳ Loading BG...', width / 2, height / 2);
    }
    targetCtx.restore();
}

/**
 * Подготавливает (создает или ресайзит) offscreen canvas для кеширования.
 * @param {object} cache - Объект для хранения кеша (e.g., puzzle.board). Содержит { canvas, ctx, renderedWidth, renderedHeight, renderedDpr }.
 * @param {number} logicalWidth - Логическая ширина кешируемой области.
 * @param {number} logicalHeight - Логическая высота кешируемой области.
 * @param {number} dpr - Device Pixel Ratio.
 * @returns {CanvasRenderingContext2D | null} Контекст подготовленного offscreen canvas или null в случае ошибки.
 */
// Функция без изменений
function prepareOffscreenCanvas(cache, logicalWidth, logicalHeight, dpr) {
  if (logicalWidth <= 0 || logicalHeight <= 0) {
    console.warn('prepareOffscreenCanvas skipped: invalid dimensions.', { logicalWidth, logicalHeight });
    if (cache.canvas) {
        cache.canvas.width = 0;
        cache.canvas.height = 0;
    }
    cache.renderedWidth = 0;
    cache.renderedHeight = 0;
    return null;
  }

  const scaledWidth = Math.round(logicalWidth * dpr);
  const scaledHeight = Math.round(logicalHeight * dpr);

  const needsUpdate =
    !cache.canvas ||
    cache.canvas.width !== scaledWidth ||
    cache.canvas.height !== scaledHeight ||
    cache.renderedDpr !== dpr;

  if (needsUpdate) {
    if (!cache.canvas) {
      try {
          cache.canvas = document.createElement('canvas');
          cache.ctx = cache.canvas.getContext('2d', { alpha: true });
          if (!cache.ctx) throw new Error("Failed to get 2D context");
      } catch (error) {
           console.error("Failed to create offscreen canvas or context:", error);
           delete cache.canvas; delete cache.ctx;
           cache.renderedWidth = 0; cache.renderedHeight = 0;
           return null;
      }
    }

    cache.canvas.width = scaledWidth;
    cache.canvas.height = scaledHeight;
    cache.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    cache.renderedWidth = logicalWidth;
    cache.renderedHeight = logicalHeight;
    cache.renderedDpr = dpr;
  }

  return cache.ctx;
}